import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Bookmark, Trash2, RefreshCw, Pencil } from 'lucide-react';

/**
 * List of the user's saved Deal Scanner searches.
 * Free users see an upgrade prompt in place of this list.
 */
export default function SavedSearchList({ items, onRun, onDelete, onRename, locked, onUpgrade }) {
  const { t } = useTranslation();

  if (locked) {
    return (
      <div
        data-testid="scanner-saved-searches-locked"
        className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center bg-white/60 dark:bg-[#131824]/60"
      >
        <Bookmark className="w-8 h-8 mx-auto text-slate-400 mb-2" />
        <div className="font-semibold text-[#12355B] dark:text-white">
          {t('dealScanner.savedSearchesLockedTitle')}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
          {t('dealScanner.savedSearchesLockedDesc')}
        </p>
        <Button
          data-testid="scanner-saved-searches-upgrade"
          onClick={onUpgrade}
          className="mt-3 bg-gradient-to-r from-[#12355B] to-[#1565C0] text-white rounded-lg px-5 py-2"
        >
          {t('dealScanner.upgrade')}
        </Button>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div
        data-testid="scanner-saved-searches-empty"
        className="rounded-2xl border border-slate-200 dark:border-slate-800 p-6 text-center bg-white dark:bg-[#131824]"
      >
        <Bookmark className="w-8 h-8 mx-auto text-slate-400 mb-2" />
        <div className="font-semibold text-[#12355B] dark:text-white">
          {t('dealScanner.savedSearchesEmptyTitle')}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {t('dealScanner.savedSearchesEmptyDesc')}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="scanner-saved-searches-list" className="space-y-2">
      {items.map((s) => (
        <div
          key={s.search_id}
          data-testid={`scanner-saved-${s.search_id}`}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-4"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-[#45C04A]" />
              <div className="font-semibold text-[#12355B] dark:text-white truncate">
                {s.name}
              </div>
            </div>
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 space-x-3">
              {s.criteria?.location && <span>📍 {s.criteria.location}</span>}
              {s.criteria?.max_price && <span>💰 ≤ ${(s.criteria.max_price / 1000).toFixed(0)}K</span>}
              {s.criteria?.min_monthly_cash_flow && <span>💵 ≥ ${s.criteria.min_monthly_cash_flow}/mo</span>}
              {s.criteria?.min_cap_rate && <span>📈 ≥ {s.criteria.min_cap_rate}% cap</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid={`scanner-saved-run-${s.search_id}`}
              onClick={() => onRun(s)}
              className="rounded-lg px-3 py-1.5 text-xs bg-[#12355B] dark:bg-[#45C04A] dark:text-[#12355B] text-white"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> {t('dealScanner.runSearch')}
            </Button>
            <Button
              data-testid={`scanner-saved-rename-${s.search_id}`}
              onClick={() => onRename(s)}
              variant="outline"
              className="rounded-lg px-3 py-1.5 text-xs"
            >
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button
              data-testid={`scanner-saved-delete-${s.search_id}`}
              onClick={() => onDelete(s)}
              variant="ghost"
              className="rounded-lg px-3 py-1.5 text-xs text-rose-500"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
